import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IconWhatsappFilled } from 'takeat-design-system-ui-kit';
import { MdClose } from 'react-icons/md';
import {
  ModalWrapper,
  ModalContent,
  ActionButton,
  ProgressBarContainer,
  ProgressBar,
  CirclesWrapper,
  Circle,
  TextWrapper,
  Title,
  Subtitle,
  CloseButton,
  Body,
  InfoCard,
  InstructionWrapper,
  InstructionText,
  ProgressBarWrapper,
} from './styles';

const DEFAULT_DURATION = 5000;

export const WppModal = ({
  isOpen,
  onClose,
  onAction,
  duration = DEFAULT_DURATION,
  onAutoClose,
  title = 'Atenção:',
  subtitle = 'Sua conexão com WhatsApp foi interrompida!',
  instruction = 'Clique em "Reconectar" para reativar o atendimento e evitar perda de mensagens.',
  actionLabel = 'Reconectar',
  showProgressBar,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [progress, setProgress] = useState(100);

  const animationFrameId = useRef(null);
  const animateRef = useRef(null);
  const startTimeRef = useRef(null);
  const elapsedTimeRef = useRef(0);
  const isPausedRef = useRef(false);
  const prevIsOpen = useRef(isOpen);

  const hasDuration = typeof duration === 'number' && duration > 0;
  const shouldShowProgressBar =
    showProgressBar !== undefined ? showProgressBar : hasDuration;

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 700);
  }, [onClose]);

  useEffect(() => {
    if (!hasDuration) {
      setProgress(100);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
      return undefined;
    }

    if (isOpen) {
      setIsClosing(false);
      startTimeRef.current = null;
      elapsedTimeRef.current = 0;
      setProgress(100);

      animateRef.current = (timestamp) => {
        if (!startTimeRef.current) {
          startTimeRef.current = timestamp - elapsedTimeRef.current;
        }

        if (!isPausedRef.current) {
          const currentElapsedTime = timestamp - startTimeRef.current;
          const progressPercent = Math.min(currentElapsedTime / duration, 1);
          setProgress(100 - progressPercent * 100);

          if (currentElapsedTime >= duration) {
            if (typeof onAutoClose === 'function') {
              try {
                onAutoClose();
              } catch (e) {
                /* ignore */
              }
            }
            handleClose();
            return;
          }
        }

        animationFrameId.current = requestAnimationFrame(animateRef.current);
      };

      animationFrameId.current = requestAnimationFrame(animateRef.current);
    } else if (!isOpen && !isClosing) {
      if (prevIsOpen.current) {
        setIsClosing(true);
      }
    }

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
    };
  }, [duration, handleClose, hasDuration, isClosing, isOpen, onAutoClose]);

  useEffect(() => {
    if (!isOpen && prevIsOpen.current && !isClosing) {
      setIsClosing(true);
    }
    prevIsOpen.current = isOpen;
  }, [isClosing, isOpen]);

  const handleMouseEnter = () => {
    if (!hasDuration) {
      return;
    }
    if (!isPausedRef.current) {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
      if (startTimeRef.current) {
        elapsedTimeRef.current = performance.now() - startTimeRef.current;
      }
      isPausedRef.current = true;
    }
  };

  const handleMouseLeave = () => {
    if (!hasDuration) {
      return;
    }
    if (isPausedRef.current) {
      isPausedRef.current = false;
      startTimeRef.current = null;
      if (!animationFrameId.current && animateRef.current) {
        animationFrameId.current = requestAnimationFrame(animateRef.current);
      }
    }
  };

  if (!isOpen && !isClosing) {
    return null;
  }

  return (
    <ModalWrapper
      $isClosing={isClosing}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ModalContent>
        <CirclesWrapper>
          <Circle size={52} />
          <Circle size={35.23} />
          <IconWhatsappFilled fontSize={20} />
        </CirclesWrapper>
        <TextWrapper>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </TextWrapper>
        <CloseButton onClick={handleClose}>
          <MdClose color="#545454" size={24} />
        </CloseButton>
      </ModalContent>
      <Body>
        <InfoCard>
          <InstructionWrapper>
            <InstructionText>{instruction}</InstructionText>
            <ActionButton
              type="button"
              onClick={() => {
                if (typeof onAction === 'function') {
                  onAction();
                }
              }}
            >
              {actionLabel}
            </ActionButton>
          </InstructionWrapper>
        </InfoCard>
      </Body>
      {shouldShowProgressBar && (
        <ProgressBarContainer>
          <ProgressBarWrapper>
            <ProgressBar progress={progress} />
          </ProgressBarWrapper>
        </ProgressBarContainer>
      )}
    </ModalWrapper>
  );
};

const WppReconnectModal = ({
  isOpen,
  onClose,
  onReconnect,
  onAutoClose,
  duration = DEFAULT_DURATION,
}) => (
  <WppModal
    isOpen={isOpen}
    onClose={onClose}
    onAction={onReconnect}
    onAutoClose={onAutoClose}
    duration={duration}
  />
);

export default WppReconnectModal;
